import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from '../axios'

export type SubscriptionLevel = 'FREE' | 'BASIC' | 'PREMIUM' | 'ENTERPRISE'
export type SubscriptionStatus = 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'CANCELLED'

export interface UserType {
  id: string
  username: string
  email: string
  subscriptionLevel: SubscriptionLevel
  subscriptionStatus: SubscriptionStatus
  roles: string[]
}

interface AuthState {
  isAuthenticated: boolean
  user: UserType | null
  token: string | null
  loading: 'idle' | 'pending' | 'succeeded' | 'failed'
  initialized: boolean
  error: string | null
}

interface LoginPayload {
  username: string
  password: string
}

interface AuthApiResponse {
  accessToken: string
  id: string
  username: string
  email: string
  roles: string[]
  subscriptionLevel: SubscriptionLevel
  subscriptionStatus: SubscriptionStatus
}

interface AuthResult {
  token: string
  user: UserType
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: 'idle',
  initialized: false,
  error: null,
}

export const login = createAsyncThunk<AuthResult, LoginPayload>(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await axios.post<AuthApiResponse>('/api/auth/signin', { username, password })
      const { accessToken, id, username: responseUsername, email, roles, subscriptionLevel, subscriptionStatus } = response.data

      if (accessToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
      }

      return {
        token: accessToken,
        user: { id, username: responseUsername, email, roles, subscriptionLevel, subscriptionStatus },
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed')
    }
  }
)

export const refreshAuth = createAsyncThunk<AuthResult>(
  'auth/refresh',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post<AuthApiResponse>('/api/auth/refresh')
      const { accessToken, id, username, email, roles, subscriptionLevel, subscriptionStatus } = response.data

      if (accessToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
      }

      return {
        token: accessToken,
        user: { id, username, email, roles, subscriptionLevel, subscriptionStatus },
      }
    } catch (error: any) {
      return rejectWithValue('Session expired')
    }
  }
)

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await axios.post('/api/auth/logout')
    } catch {
      // best-effort
    }
    delete axios.defaults.headers.common['Authorization']
    return null
  }
)

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<UserType>('/api/auth/me')
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get user')
    }
  }
)

export const updateSubscriptionStatus = createAsyncThunk(
  'auth/updateSubscriptionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<UserType>('/api/auth/me')
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update subscription status')
    }
  }
)

const applyAuthResult = (state: AuthState, result: AuthResult) => {
  state.isAuthenticated = true
  state.user = result.user
  state.token = result.token
  state.loading = 'succeeded'
  state.error = null
  state.initialized = true
}

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
      state.isAuthenticated = true
      axios.defaults.headers.common['Authorization'] = `Bearer ${action.payload}`
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = 'pending'
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        applyAuthResult(state, action.payload)
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = 'failed'
        state.error = (action.payload as string) || 'Login failed'
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.initialized = true
      })
      .addCase(refreshAuth.pending, (state) => {
        state.loading = 'pending'
      })
      .addCase(refreshAuth.fulfilled, (state, action) => {
        applyAuthResult(state, action.payload)
      })
      .addCase(refreshAuth.rejected, (state) => {
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.loading = 'idle'
        state.initialized = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.error = null
        state.initialized = true
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = 'pending'
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isAuthenticated = true
        state.user = action.payload
        state.loading = 'succeeded'
        state.initialized = true
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = 'failed'
        state.error = action.payload as string
        state.isAuthenticated = false
        state.user = null
        state.initialized = true
      })
  },
})

export const { setToken } = authSlice.actions
export default authSlice.reducer
